import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import config from "@/config";
import { stream_video } from "../API";

interface VideoPlayerProps {
	fileName: string;
	location?: string;
}

export function VideoPlayer({
	fileName,
	location = "resources",
}: VideoPlayerProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [videoUrl, setVideoUrl] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string>("");

	const loadVideo = async () => {
		setLoading(true);
		setError("");

		try {
			const response = await stream_video(fileName, location);
			console.log(response);

			const fullVideoUrl = buildFullVideoUrl(response.video_url);
			console.log("Final video URL:", fullVideoUrl);
			setVideoUrl(fullVideoUrl);
		} catch (err) {
			console.error("Error loading video:", err);
			setError("Network error occurred");
		} finally {
			setLoading(false);
		}
	};

	const handleVideoError = (
		e: React.SyntheticEvent<HTMLVideoElement, Event>,
	) => {
		console.error("Video error:", e);
		setError("Failed to load video");
	};

	useEffect(() => {
		loadVideo();
	}, [fileName, location]);

	return (
		<VideoPlayerContainer>
			<VideoPlayerContent
				loading={loading}
				error={error}
				onRetry={loadVideo}
				videoUrl={videoUrl}
				videoRef={videoRef}
				onVideoError={handleVideoError}
			/>
		</VideoPlayerContainer>
	);
}

// Build full video URL from relative or absolute path
function buildFullVideoUrl(videoUrl: string): string {
	if (videoUrl.startsWith("/")) {
		return `${config.apiBaseUrl}${videoUrl}`;
	}
	return videoUrl;
}

// Main container for video player
function VideoPlayerContainer({ children }: { children: React.ReactNode }) {
	return <div className="w-full">{children}</div>;
}

// Content switcher based on loading/error/video states
function VideoPlayerContent({
	loading,
	error,
	onRetry,
	videoUrl,
	videoRef,
	onVideoError,
}: {
	loading: boolean;
	error: string;
	onRetry: () => void;
	videoUrl: string;
	videoRef: React.RefObject<HTMLVideoElement>;
	onVideoError: (e: React.SyntheticEvent<HTMLVideoElement, Event>) => void;
}) {
	if (loading) {
		return <LoadingState />;
	}

	if (error) {
		return <ErrorState error={error} onRetry={onRetry} />;
	}

	return (
		<VideoElement
			videoRef={videoRef}
			videoUrl={videoUrl}
			onVideoError={onVideoError}
		/>
	);
}

// Loading state component
function LoadingState() {
	return (
		<LoadingContainer>
			<LoadingText>Loading video...</LoadingText>
		</LoadingContainer>
	);
}

// Loading container with centering
function LoadingContainer({ children }: { children: React.ReactNode }) {
	return <div className="flex justify-center p-4">{children}</div>;
}

// Loading text component
function LoadingText({ children }: { children: React.ReactNode }) {
	return <div>{children}</div>;
}

// Error state component
function ErrorState({
	error,
	onRetry,
}: {
	error: string;
	onRetry: () => void;
}) {
	return (
		<ErrorContainer>
			<ErrorMessage message={error} />
			<RetryButton onRetry={onRetry} />
		</ErrorContainer>
	);
}

// Error container with centering
function ErrorContainer({ children }: { children: React.ReactNode }) {
	return <div className="text-center p-4">{children}</div>;
}

// Error message component
function ErrorMessage({ message }: { message: string }) {
	return <p className="text-red-500 mb-2">{message}</p>;
}

// Retry button component
function RetryButton({ onRetry }: { onRetry: () => void }) {
	return <Button onClick={onRetry}>Retry</Button>;
}

// Video element component with all handlers
function VideoElement({
	videoRef,
	videoUrl,
	onVideoError,
}: {
	videoRef: React.RefObject<HTMLVideoElement>;
	videoUrl: string;
	onVideoError: (e: React.SyntheticEvent<HTMLVideoElement, Event>) => void;
}) {
	const handleLoadStart = () => {
		console.log("Video loading started");
	};

	const handleCanPlay = () => {
		console.log("Video can play");
	};

	return (
		<video
			ref={videoRef}
			controls
			className="w-full h-auto max-h-96"
			preload="metadata"
			onError={onVideoError}
			onLoadStart={handleLoadStart}
			onCanPlay={handleCanPlay}
		>
			<VideoSource videoUrl={videoUrl} />
			<VideoFallbackMessage />
		</video>
	);
}

// Video source element
function VideoSource({ videoUrl }: { videoUrl: string }) {
	return <source src={videoUrl} type="video/mp4" />;
}

// Fallback message for unsupported browsers
function VideoFallbackMessage() {
	return <>Your browser does not support the video tag.</>;
}
